import { useGetPaymentPlans } from '../../../../../domain/PermanentJobs/useCases';
import { CustomSelect } from '../../../UsersList/components/CustomSelect';

/**
 * PlansCombobox component for selecting payment plans
 * Automatically fetches payment plans and displays them in a dropdown
 *
 * @param {Object} props - Component props
 * @param {Function} props.onChange - Callback function that receives an object { value: string, label: string }
 *   - value: Empty string for "All" option, or plan ID for specific plan
 *   - label: Display text (e.g., "All", "Basic Plan", etc.)
 * @param {Object} [props.lang] - Language/text configuration
 * @param {string} [props.lang.allOptionsText='All'] - Text for the "All" option (defaults to "All")
 * @param {string} [props.value=''] - Currently selected plan ID (empty string for "All")
 * @param {string} [props.label] - Label text displayed above the select
 * @param {boolean} [props.isError=false] - Whether the select is in error state
 * @param {boolean} [props.isReadonly=false] - Whether the select is read-only
 *
 * @example
 * <PlansCombobox
 *   onChange={({ value, label }) => {
 *     console.log('Selected:', value, label);
 *     // value: '' for All, or plan ID
 *     // label: 'All' or plan name
 *   }}
 *   lang={{ allOptionsText: 'All Plans' }}
 *   value=""
 *   label="Select Plan"
 *   isError={false}
 *   isReadonly={false}
 * />
 */
export const PlansCombobox = ({
  label,
  lang = {},
  value = '',
  onChange,
  isError = false,
  isReadonly = false,
}) => {
  const { data: plans } = useGetPaymentPlans();

  const { allOptionsText = 'All' } = lang;

  // Prepare options with "All" option as first item
  const options = [
    { code: '', name: allOptionsText },
    ...(plans || []).map((plan) => ({
      code: plan.id,
      name: plan.name,
    })),
  ];

  const handleChange = (_id, selectedValue) => {
    const selected = options.find((option) => option.code === selectedValue);

    if (selected) {
      onChange({ value: selected.code, label: selected.name });
    }
  };

  return (
    <CustomSelect
      id="plan"
      label={label}
      options={options}
      value={value}
      onChange={handleChange}
      isError={isError}
      isReadonly={isReadonly}
    />
  );
};
